import * as React from 'react';
import { Overlay } from '../components/Overlay';
import { ThumbsGrid } from '../components/ThumbsGrid';
import Layer from '../data/Layer';
import { generateClassName, loadFile } from '../utils';
import { getStickers, GiphyUser, StickerData } from '../utils/FirebaseUtils';
import * as Actions from '../state/actions';
import { storeDispatch } from '../state/Store';
import { GiphyFetch } from '@giphy/js-fetch-api';
import { OverlaySearch } from '../components/OverlaySearch';
import { reportIssue, trackEvent } from '../utils/Tracker';
import { LoadingMore } from '../components/LoadingMore';
import { isUrlOnAWS } from 'utils/S3Utils';

const GIPHY_API_KEY = 'ir2tAjY6j9EM8lwzoQjKEtTeBRukJ8Q3';
const gf = new GiphyFetch(GIPHY_API_KEY);

// https://developers.giphy.com/explorer

const limit = 30;

const getInitialGiphy = async (offset: number = 0): Promise<StickerData[]> => {
  // add gf.emoji
  const res = await gf.trending({
    type: 'stickers',
    limit,
    offset
  });
  console.log(res);
  return res.data.map(item => {
    return {
      url: item.images.original.url,
      thumbnailUrl: item.images.fixed_width.url,
      giphyUser: item.user as any
    };
  });
};

const searchGiphy = async (
  query: string,
  offset: number = 0
): Promise<StickerData[]> => {
  const res = await gf.search(query, { type: 'stickers', offset, limit });
  return res.data.map(item => {
    return {
      url: item.images.original.url,
      thumbnailUrl: item.images.fixed_width.url,
      giphyUser: item.user as any
    };
  });
};

let ownStickers: StickerData[];

interface AttributionProps {
  data: GiphyUser;
}
const GiphyAttribution: React.FunctionComponent<AttributionProps> = props => {
  const { data } = props;
  return (
    <a
      href={data.profile_url}
      target="_blank"
      rel="noopener noreferrer"
      className="giphy-attribution"
    >
      <img
        className="avatar"
        src={data.avatar_url}
        // crossOrigin="anonymous"
      />
      <div className="name">{data.display_name}</div>
      {data.is_verified && (
        <svg
          className="giphy-verified-badge"
          height="14"
          width="19px"
          viewBox="0 0 19 17"
        >
          <path
            className="giphy-verified-checkmark"
            d="M9.32727273,9.44126709 L9.32727273,3.03016561 L6.55027155,3.03016561 L6.55027155,10.8150746 L6.55027155,12.188882 L12.1042739,12.188882 L12.1042739,9.44126709 L9.32727273,9.44126709 Z"
            fill="#1a1a1a"
            transform="translate(9.327273, 7.609524) scale(-1, 1) rotate(-45.000000) translate(-9.327273, -7.609524) "
          />
          <g transform="translate(-532.000000, -466.000000)" fill="white">
            <g transform="translate(141.000000, 235.000000)">
              <g transform="translate(264.000000, 0.000000)">
                <g transform="translate(10.000000, 224.000000)">
                  <g transform="translate(114.000000, 2.500000)">
                    <path d="M15.112432,4.80769231 L16.8814194,6.87556817 L19.4157673,7.90116318 L19.6184416,10.6028916 L21.0594951,12.9065042 L19.6184416,15.2101168 L19.4157673,17.9118452 L16.8814194,18.9374402 L15.112432,21.0053161 L12.4528245,20.3611511 L9.79321699,21.0053161 L8.02422954,18.9374402 L5.48988167,17.9118452 L5.28720734,15.2101168 L3.84615385,12.9065042 L5.28720734,10.6028916 L5.48988167,7.90116318 L8.02422954,6.87556817 L9.79321699,4.80769231 L12.4528245,5.4518573 L15.112432,4.80769231 Z M17.8163503,10.8991009 L15.9282384,9.01098901 L11.5681538,13.3696923 L9.68115218,11.4818515 L7.81302031,13.3499833 L9.7011322,15.2380952 L11.5892441,17.1262071 L17.8163503,10.8991009 Z" />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>
      )}
    </a>
  );
};

interface ItemProps {
  data: StickerData;
  onClick: (sticker: StickerData) => void;
}

class ThumbView extends React.Component<ItemProps> {
  containerEl = React.createRef<HTMLDivElement>();

  didClick = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!this.containerEl.current) {
      return;
    }
    if (this.props.data.giphyUser) {
      const attributionEl =
        this.containerEl.current.querySelector('.giphy-attribution');
      if (attributionEl && attributionEl.contains(target)) {
        return;
      }
    }
    this.props.onClick(this.props.data);
  };

  render() {
    const g = this.props.data;
    return (
      <div onClick={this.didClick} ref={this.containerEl}>
        <div
          className={generateClassName('thumbs-grid-thumb', {
            sticker: true
          })}
        >
          <img
            src={g.thumbnailUrl}
            // crossOrigin="anonymous"
          />
          {g.giphyUser && <GiphyAttribution data={g.giphyUser} />}
        </div>
      </div>
    );
  }
}

interface Props {
  layer?: Layer;
}

interface State {
  stickers: StickerData[];
  query: string | null;
  isAdding: boolean;
  isSearching: boolean;
  isLoadingMorePhotos: boolean;
  offset: number;
}

export class StickerSelector extends React.Component<Props, State> {
  state: State = {
    stickers: [],
    query: null,
    isAdding: false,
    isSearching: false,
    isLoadingMorePhotos: false,
    offset: 0
  };

  async componentDidMount() {
    let stickers: StickerData[] = [];
    try {
      ownStickers = await getStickers();
      stickers = stickers.concat(ownStickers);
    } catch {
      // do nothing. Report?
    }
    try {
      stickers = stickers.concat(await getInitialGiphy());
    } catch {
      // do nothing. Report?
    }
    this.setState({ stickers });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.stickers.length !== this.state.stickers.length) {
      this.gridDidScroll();
    }
  }

  hasReachedTheBottom = (): boolean => {
    const element = document.querySelector('.overlay-content');
    if (!element) {
      return true;
    }
    return element.scrollHeight - element.scrollTop === element.clientHeight;
  };

  didSelectSticker = async (sticker: StickerData) => {
    const baseUrl = sticker.url.split('?')[0];
    // console.log('Selected sticker: ' + baseUrl);
    trackEvent('Add Sticker', { url: baseUrl });
    const extension = baseUrl.split('.').pop();
    this.setState({ isAdding: true });
    try {
      const blob = await loadFile(
        sticker.url + (isUrlOnAWS(sticker.url) ? `?${Date.now()}` : '')
      );
      const file = new File([blob], 'sticker.' + extension, {
        type: 'image/' + extension
      });
      storeDispatch(
        Actions.addLocalGraphics([file], this.props.layer, false, {
          x: 420,
          y: 420
        })
      );
    } catch {
      this.setState({ isAdding: false });
    }
  };

  loadMorePhotos = async () => {
    const { query, stickers } = this.state;
    this.setState({ isLoadingMorePhotos: true });
    const offset = this.state.offset + limit;
    // console.log(
    //   `LOAD MORE PHOTOS: current stickers: ${stickers.length} offset:${offset}`
    // );
    let newStickers: StickerData[];
    if (query) {
      newStickers = await searchGiphy(query, offset);
    } else {
      newStickers = await getInitialGiphy(offset);
    }
    newStickers = newStickers.filter(p => {
      for (const ep of stickers) {
        if (ep.url === p.url) {
          return false;
        }
      }
      return true;
    });
    this.setState({
      isLoadingMorePhotos: false,
      offset,
      stickers: stickers.concat(newStickers)
    });
  };

  onSearchSubmit = async (value: string) => {
    value = value.trim().toLowerCase();
    if (value.length) {
      // console.log(`Search: "query"`);
      this.setState({
        query: value,
        isSearching: true,
        offset: 0
      });
      let items: StickerData[] = [];
      try {
        items = items.concat(
          ownStickers.filter(s => s.keywords && s.keywords.includes(value))
        );
        items = items.concat(await searchGiphy(value));
      } catch (e) {
        alert(e);
        reportIssue(e);
      }
      this.setState({
        isSearching: false,
        stickers: items
      });
    }
  };

  gridDidScroll = (event?: React.UIEvent<HTMLDivElement>) => {
    const { isLoadingMorePhotos, isSearching } = this.state;
    // console.log('--- grid --- scroll');
    if (this.hasReachedTheBottom() && !isLoadingMorePhotos && !isSearching) {
      this.loadMorePhotos();
    }
  };

  render() {
    const { isAdding, query, stickers, isSearching, isLoadingMorePhotos } =
      this.state;
    if (!ownStickers || !ownStickers.length) {
      return (
        <Overlay>
          <h1>Loading...</h1>
        </Overlay>
      );
    }

    if (isAdding) {
      return (
        <Overlay>
          <h1>Adding sticker...</h1>
        </Overlay>
      );
    }

    let title = query
      ? stickers.length
        ? `Showing results for "${query}"`
        : `No result found for "${query}"`
      : 'Select sticker';

    if (isSearching) {
      title = query ? `Searching "${query}"...` : 'Loading popular photos...';
    }

    return (
      <Overlay onScroll={this.gridDidScroll} alignTop={true} fullWidth={true}>
        <ThumbsGrid
          title={title}
          small={true}
          subtitleChild={
            <div>
              <OverlaySearch
                onSubmit={this.onSearchSubmit}
                focusAtStart={true}
              />
              <a
                href="https://giphy.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginBottom: 20, display: 'block' }}
              >
                <img
                  src="/media/Poweredby_640px-White_HorizText.png"
                  width="160"
                />
              </a>
            </div>
          }
        >
          {stickers.map((g, index) => (
            <ThumbView data={g} key={g.url} onClick={this.didSelectSticker} />
          ))}
        </ThumbsGrid>
        {isLoadingMorePhotos && <LoadingMore />}
      </Overlay>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modals/StickerSelector.tsx